import React, { useContext, type MouseEvent } from 'react';
import PropTypes from 'prop-types';
import { Handle, Position, useReactFlow, type NodeProps } from 'reactflow';
import type { FlowChildNode, FlowNodeData } from '../../../edit/flowGraph';
import { SubflowNavigationContext, formatLockTag } from '../Flow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderTree, faLock } from '@fortawesome/free-solid-svg-icons';

import iconSql from '../../../../../../../../assets/img/icons/sql-database-generic.svg';
import iconSap from '../../../../../../../../assets/img/icons/sap.svg';
import iconRest from '../../../../../../../../assets/img/icons/rest.png';
import iconFile from '../../../../../../../../assets/img/icons/file.png';

const icons = {
  sql: iconSql,
  sap_abap_run: iconSap,
  rest: iconRest,
  file: iconFile,
};

const StandardNode = (node: NodeProps<FlowNodeData>) => {
  const { type, id, data } = node;
  const reactFlowInstance = useReactFlow();
  const subflowNav = useContext(SubflowNavigationContext);
  const { hasChildren, status, sub_status, draggable, activated_handles = [], submit_disabled, submit_and_forget, definition_locks = [] } = data;
  // A nullable column, filled from the node type's default_handles on upsert, so this guards older rows only.
  const handleFields = data.handles ?? [];

  const iconname = (icons as Record<string, string>)[type] || iconFile; // Select icon based on type
  const isFlowNode = type === 'flow';
  const waitingSubStatuses: readonly (string | null | undefined)[] = ['Console', 'EventWait', 'LockWait', 'QueueWait'];
  const effectiveStatus = waitingSubStatuses.includes(sub_status) ? 'waiting' : status;

  // Base height is the initial height of the node, without options
  const baseHeight = 28;
  const minHeight = 55; // Minimum height for the node

  const hasHandleFields = handleFields.length;
  // Calculate the total height based on the number of handles/options
  const totalHeight = baseHeight + hasHandleFields * 27;
  const defaultClass = hasHandleFields === 1 ? 'default' : '';

  // Access full node object from ReactFlow store to get node_definition_id
  const fullNode = reactFlowInstance.getNode(id) as FlowChildNode | undefined;
  const canOpenInNewTab = !!fullNode?.node_definition_id;
  const isExpandable = isFlowNode || !!hasChildren;

  const expandFn = data.onExpandNode ?? subflowNav?.onExpandNode ?? null;
  const canExpandInline = isExpandable && !!expandFn;

  const handleOpenInNewTab = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const nodeDefId = fullNode?.node_definition_id;
    const nodeName = fullNode?.node_definition_name || data.label;

    if (!nodeDefId) {
      console.warn('Cannot open node in new tab: missing node_definition_id');
      return;
    }

    // The editor loads by id; the name segment only titles its tab. It is missing only for a definition without a name,
    // which the API does not create (nap-backend #1811).
    window.open(`/node_editor/${nodeDefId}/${encodeURIComponent(String(nodeName))}`, '_blank');
  };

  const handleExpandClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (expandFn) {
      expandFn(id);
    }
  };

  return (
    <div
      data-id={id}
      className={`editor-node ${effectiveStatus ? effectiveStatus : ''} ${sub_status ? `sub-status-${sub_status.toLowerCase()}` : ''} ${submit_disabled === true ? 'submit-disabled' : ''} ${submit_and_forget === true ? 'submit-and-forget' : ''}`}
      title={
        submit_and_forget === true
          ? 'Submit and forget — detached root submit; flow does not wait'
          : submit_disabled === true
            ? 'Disabled by default — skipped on every run'
            : undefined
      }
      style={{ height: `${Math.max(totalHeight, minHeight)}px` }}
    >
      <div
        data-id={id}
        style={{
          paddingRight: hasHandleFields ? '75px' : '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ pointerEvents: 'none' }}>
          {isExpandable ? (
            <span
              className="flow-icon-clickable"
              onClick={canExpandInline ? handleExpandClick : undefined}
              style={{
                pointerEvents: 'all',
                cursor: canExpandInline ? 'pointer' : 'default',
                opacity: canExpandInline ? 1 : 0.4,
              }}
              title={canExpandInline ? (data.isExpanded ? 'Collapse subflow' : 'Expand subflow') : undefined}
            >
              <FontAwesomeIcon icon={faFolderTree} size="sm" />
            </span>
          ) : (
            <img src={iconname} alt="icon" className="handle-icon" />
          )}{' '}
          {data.label}
          {definition_locks.length > 0 && (
            <span className="ms-1 text-warning" style={{ pointerEvents: 'all' }} title={`Definition locks: ${definition_locks.map(formatLockTag).join(', ')}`}>
              <FontAwesomeIcon icon={faLock} size="xs" />
            </span>
          )}
          {submit_and_forget === true && <span className="flow-type-label">(Submit &amp; forget)</span>}
          {isExpandable && <span className="flow-type-label">(Flow)</span>}
        </span>
        {canOpenInNewTab && (
          <button className="open-new-tab-btn" onClick={handleOpenInNewTab} style={{ pointerEvents: 'all' }} title="Open in New Tab">
            ↗
          </button>
        )}
      </div>
      <div className="pe-none editor-node-id">ID: {id}</div>
      {data.description && (
        <div className="pe-none" style={{ paddingRight: hasHandleFields ? '75px' : '0' }}>
          <span className="description" style={{ fontSize: '0.75em' }}>
            {data.description}
          </span>
        </div>
      )}
      {!!hasChildren && <div className="pe-none">+</div>}
      <Handle type="target" position={Position.Left} />{' '}
      <div className={`handles-container pe-none ${defaultClass}`}>
        {!!hasHandleFields &&
          handleFields.map((handle, index) => {
            const isActivatedHandle = activated_handles.some((itemId) => itemId === handle.id);

            return (
              <div className="handle-wrapper" key={handle.id || index}>
                <Handle id={handle.id} type="source" position={Position.Right} className={`handle ${isActivatedHandle ? 'activated' : ''}`} />
                <span className="handle-label me-2">{handle.label}</span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

StandardNode.propTypes = {
  data: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

export default StandardNode;
